import { useId, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Button, TextInput } from '@carbon/react';
import { Location } from '@carbon/react/icons';
import ErrorToast from 'components/error-toast';
import { appStore } from 'components/map';
import { coordinatesCursorAtom } from './atoms';
import { useLocationSuggestions, usePinActions } from './hooks';
import {
  formatDecimalPair,
  type LngLat,
  type LocationSuggestion,
  optionId,
  parseCoordinates,
} from './coordinate-utils';
import { LocationSuggestions } from './location-suggestions';
import styles from './coordinate-pins.module.scss';

/**
 * The card's Location field and Drop pin.
 *
 * The field shows the cursor's spot over the map, through every pin a click drops;
 * focusing it holds that reading for editing. Coordinates drop where they say. Other
 * text suggests matching wells, places and survey areas below the field, and Drop pin
 * (or Enter) takes the highlighted or first one.
 *
 * @return {JSX.Element} The field, its suggestions and any error toast.
 */
export function LocationField(): JSX.Element {
  const cursor = useAtomValue(coordinatesCursorAtom, appStore);
  const { dropPin } = usePinActions();
  const fieldId = useId();
  const listId = `${fieldId}-suggestions`;

  /** The user's edit of the field; null while the field follows the cursor. */
  const [draft, setDraft] = useState<string | null>(null);
  /** Whether the user typed since focusing; the cursor moves the reading regardless. */
  const [edited, setEdited] = useState(false);
  const [error, setError] = useState<{ title: string; detail: string } | null>(
    null
  );
  const [focused, setFocused] = useState(false);
  /** Escape hides the list until the text changes. */
  const [listHidden, setListHidden] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const { suggestions, loading, failed, searchNow } =
    useLocationSuggestions(draft);
  const listOpen =
    focused && !listHidden && (loading || failed || suggestions.length > 0);

  const following = draft === null;
  const reading = cursor ? formatDecimalPair(cursor) : '';
  const value = draft ?? reading;

  /**
   * Drops a pin and flies there, leaving the field to follow the cursor again.
   *
   * @param {LocationSuggestion['lngLat']} lngLat - Where.
   * @param {string} [label] - The well or place there, if it is one.
   */
  const drop = (lngLat: LngLat, label?: string) => {
    setError(null);
    setDraft(null);
    setHighlight(-1);
    dropPin(lngLat, { fly: true, label });
  };

  /**
   * Drops a pin on a suggestion, first finding the spot for a survey area, or toasts
   * when it has none.
   *
   * @param {LocationSuggestion} suggestion - The well, place or survey area picked.
   */
  const pick = async (suggestion: LocationSuggestion) => {
    const lngLat =
      suggestion.lngLat ?? (await suggestion.locate?.().catch(() => null));
    if (lngLat) {
      drop(lngLat, suggestion.title);
      return;
    }
    setError({
      title: "Couldn't find that location",
      detail: `${suggestion.title} has no shape on the map to pin.`,
    });
  };

  /**
   * Drops a pin at the field's coordinates, else on the highlighted or first match
   * (waiting for the search if it's still running), or toasts why it can't.
   */
  const submit = async () => {
    if (value.trim() === '') return;
    const parsed = parseCoordinates(value);
    if (parsed.ok) {
      drop(parsed.lngLat);
      return;
    }
    const found = suggestions[highlight]
      ? { suggestions: [suggestions[highlight]], failed: false }
      : await searchNow();
    const [match] = found.suggestions;
    if (match) {
      await pick(match);
      return;
    }
    // Text with a number in it was most likely meant as coordinates.
    setError(
      found.failed
        ? {
            title: "Couldn't search",
            detail:
              'Wells, places and survey areas are unavailable right now. Try again, or enter coordinates.',
          }
        : /\d/.test(value)
          ? { title: "Couldn't read those coordinates", detail: parsed.error }
          : {
              title: "Couldn't find that location",
              detail: `No wells, places or survey areas match "${value.trim()}".`,
            }
    );
  };

  return (
    <>
      {error && (
        <ErrorToast
          message={error.title}
          subtitle={error.detail}
          onClose={() => {
            setError(null);
            return true;
          }}
        />
      )}
      <div className={styles.fieldRow}>
        <TextInput
          id={fieldId}
          labelText="Location"
          helperText="Click the map, or enter coordinates, a well, a place or a survey area."
          value={value}
          className={following ? styles.following : undefined}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={listOpen}
          aria-controls={listId}
          aria-activedescendant={
            listOpen && suggestions[highlight]
              ? optionId(listId, suggestions[highlight])
              : undefined
          }
          onFocus={() => {
            setFocused(true);
            // Holds the live reading in place so it can be edited, not replaced.
            if (draft === null) {
              setDraft(reading);
              setEdited(false);
            }
          }}
          onBlur={() => {
            setFocused(false);
            // An untouched or emptied field goes back to following the cursor.
            if (draft === '' || !edited) setDraft(null);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            } else if (listOpen && e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((i) => Math.min(i + 1, suggestions.length - 1));
            } else if (listOpen && e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((i) => Math.max(i - 1, -1));
            } else if (listOpen && e.key === 'Escape') {
              // Closes the list, not the card.
              e.stopPropagation();
              setListHidden(true);
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDraft(e.target.value);
            setEdited(true);
            setListHidden(false);
            setHighlight(-1);
            if (error) setError(null);
          }}
        />
        <Button
          kind="primary"
          size="md"
          renderIcon={Location}
          disabled={value.trim() === ''}
          className={styles.dropButton}
          onClick={() => void submit()}
        >
          Drop pin
        </Button>
      </div>
      {listOpen && (
        <LocationSuggestions
          id={listId}
          suggestions={suggestions}
          highlight={highlight}
          failed={failed}
          onHighlight={setHighlight}
          onPick={(suggestion) => void pick(suggestion)}
        />
      )}
    </>
  );
}
